import { useEffect, useState, useCallback } from "react";
import {
    FaTrash,
    FaSpinner,
    FaBroom,
    FaUserCircle
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getMenuPermissions } from "../utils/auth";

import "./FaceDetections.css";

const API_URL = process.env.REACT_APP_API_URL;

function FaceDetections() {

    // Shares the "robot_fleet" menu_key - same reasoning as Category
    // Master sharing POI Location's permission: if you can monitor
    // the fleet, you can see what it's seen.
    const perms = getMenuPermissions("robot_fleet");

    const [detections, setDetections] = useState([]);

    const [isLoading, setIsLoading] = useState(false);

    const [robotSnFilter, setRobotSnFilter] = useState("");

    const [fromDate, setFromDate] = useState("");

    const [toDate, setToDate] = useState("");

    const [deletingId, setDeletingId] = useState(null);

    const [isCleaningUp, setIsCleaningUp] = useState(false);

    useEffect(() => {

        loadDetections();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadDetections = useCallback(async () => {

        setIsLoading(true);

        try {

            const params = new URLSearchParams();

            if (robotSnFilter) params.append("robot_sn", robotSnFilter);

            if (fromDate) params.append("from_date", fromDate);

            if (toDate) params.append("to_date", toDate);

            const res = await fetch(`${API_URL}/portal/face-detections?${params.toString()}`);

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setDetections(data || []);

        } catch (err) {

            console.error("Failed to load face detections:", err);

            setDetections([]);

            toast.error("Unable to load face detections. Please try again.");

        } finally {

            setIsLoading(false);

        }

    }, [robotSnFilter, fromDate, toDate]);

    const handleDelete = async (id) => {

        const ok = window.confirm("Delete this face photo? This cannot be undone.");

        if (!ok) return;

        setDeletingId(id);

        try {

            const res = await fetch(`${API_URL}/portal/face-detections/${id}`, { method: "DELETE" });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success(data.message || "Deleted successfully");

                loadDetections();

            } else {

                toast.error(data.message || "Failed to delete");

            }

        } catch (err) {

            console.error("Failed to delete face detection:", err);

            toast.error("Something went wrong while deleting");

        } finally {

            setDeletingId(null);

        }

    };

    const handleCleanup = async () => {

        const ok = window.confirm(
            "Run retention cleanup now? This permanently deletes every face photo older than the configured retention window (FACE_PHOTO_RETENTION_DAYS)."
        );

        if (!ok) return;

        setIsCleaningUp(true);

        try {

            const res = await fetch(`${API_URL}/portal/face-detections/cleanup`, { method: "POST" });

            const data = await res.json();

            toast.success(data.message);

            loadDetections();

        } catch (err) {

            console.error("Failed to run cleanup:", err);

            toast.error("Something went wrong while running cleanup");

        } finally {

            setIsCleaningUp(false);

        }

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <div>
                    <h2><FaUserCircle /> Face Detections</h2>
                    <p className="page-subtitle">
                        Face photos uploaded by each robot after an on-device detection (Robot SDK
                        "Get face photos"). These are passenger biometric photos - handle accordingly.
                        Older photos are auto-eligible for cleanup based on the server's configured
                        retention window.
                    </p>
                </div>

                {perms.delete &&

                    <button type="button" className="add-btn" onClick={handleCleanup} disabled={isCleaningUp}>
                        {isCleaningUp ? <><FaSpinner className="spin" /> Cleaning up...</> : <><FaBroom /> Run Retention Cleanup</>}
                    </button>

                }

            </div>

            <div className="face-filter-row">

                <input
                    placeholder="Filter by Robot SN"
                    value={robotSnFilter}
                    onChange={(e) => setRobotSnFilter(e.target.value)}
                />

                <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                />

                <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                />

                <button type="button" className="add-btn" onClick={loadDetections}>
                    Apply Filters
                </button>

            </div>

            {

                isLoading ?

                    <p className="empty-note"><FaSpinner className="spin" /> Loading...</p>

                    :

                    detections.length === 0 ?

                        <p className="empty-note">No face detections found for the selected filters.</p>

                        :

                        <div className="face-grid">

                            {detections.map(item => (

                                <div className="face-card" key={item.id}>

                                    <img src={item.image_url} alt={`Face detection ${item.id}`} />

                                    <div className="face-card-meta">

                                        <span className="face-card-robot">{item.robot_sn}</span>

                                        <span className="face-card-time">
                                            {new Date(item.created_at).toLocaleString()}
                                        </span>

                                        {item.distance != null && (
                                            <span className="face-card-distance">{item.distance.toFixed(1)}m</span>
                                        )}

                                    </div>

                                    {perms.delete &&

                                        <button
                                            type="button"
                                            className="icon-btn delete-btn face-card-delete"
                                            title="Delete this photo"
                                            aria-label="Delete this photo"
                                            onClick={() => handleDelete(item.id)}
                                            disabled={deletingId === item.id}
                                        >
                                            {deletingId === item.id ? <FaSpinner className="spin" /> : <FaTrash />}
                                        </button>

                                    }

                                </div>

                            ))}

                        </div>

            }

        </div>

    );

}

export default FaceDetections;
